import useUpdateFolderEntry from '@/api/mutations/folders/useUpdateFolderEntry';
import projectKeys from '@/api/queries/project/keyFactory';
import {
  GetProjectFolderEntriesQuery,
  UpdateFolderEntryInput,
  UpdateFolderEntryMutation,
} from '@/gql/graphql';
import {
  InfiniteData,
  QueryKey,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { produce } from 'immer';

const useOptimisticUpdateFolderEntry = (args?: {
  projectId?: string;
  options?: UseMutationOptions<
    UpdateFolderEntryMutation,
    unknown,
    UpdateFolderEntryInput
  >;
}) => {
  const queryClient = useQueryClient();

  return useUpdateFolderEntry({
    projectId: args?.projectId,
    options: {
      ...args?.options,
      onMutate: async (variables) => {
        if (!args?.projectId) {
          args?.options?.onMutate?.(variables);

          return {};
        }

        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: projectKeys.folderEntries(args.projectId),
        });

        // Snapshot the previous value
        const previousProjectFolderEntriesQueries = queryClient.getQueriesData<
          InfiniteData<GetProjectFolderEntriesQuery>
        >({
          queryKey: projectKeys.folderEntries(args.projectId),
        });

        // Check each cached folder entries query, regardless of input or pagination
        // Each time the updated Folder Entry appears in the cache, update its new parentFolderId
        queryClient.setQueriesData<InfiniteData<GetProjectFolderEntriesQuery>>(
          {
            queryKey: projectKeys.folderEntries(args.projectId),
          },
          produce<InfiniteData<GetProjectFolderEntriesQuery>>((oldData) => {
            if (!oldData) return;

            oldData.pages.forEach((page) => {
              if (!page.projectById.folderEntries?.items) return;

              const folderEntryToUpdate =
                page.projectById.folderEntries.items.find(
                  (i) => i.id === variables.id
                );

              if (!folderEntryToUpdate) return;

              folderEntryToUpdate.parentFolderId = variables.parentFolderId;
            });
          })
        );

        args?.options?.onMutate?.(variables);
        return { previousProjectFolderEntriesQueries };
      },
      onError: (err, newTodo, context) => {
        // If an error happens, rollback!
        const previousProjectFolderEntriesQueries =
          // @ts-expect-error context not typed
          context?.previousProjectFolderEntriesQueries as
            | [QueryKey, InfiniteData<GetProjectFolderEntriesQuery>][]
            | undefined;

        previousProjectFolderEntriesQueries?.forEach(([queryKey, oldData]) => {
          queryClient.setQueryData(queryKey, oldData);
        });

        args?.options?.onError?.(err, newTodo, context);
      },
      onSettled: (...onSettledArgs) => {
        if (args?.projectId) {
          void queryClient.invalidateQueries({
            queryKey: projectKeys.folderEntries(args.projectId),
          });
        }

        args?.options?.onSettled?.(...onSettledArgs);
      },
    },
  });
};

export default useOptimisticUpdateFolderEntry;
